/**
 * Route-level org access guard (INT-0036).
 *
 * The resource-read GETs are deliberately PUBLIC (they power the tenant
 * sites), and admin stores swallow fetch errors — so before this guard, any
 * admin could open /admin/division/<any id> and get another org's resource
 * rendered under their own org's chrome. Server-side WRITES are org-guarded
 * (2026-09-18 IDOR closure); this guard closes the read/UX half at the
 * routing layer.
 *
 * Usage (routes.tsx):
 *   <ResourceOrgGuard resource="division" param="divisionId"><DivisionPage/></ResourceOrgGuard>
 *
 * Before the page mounts, the guard fetches the resource and compares its
 * `organization` against the ACTIVE org (useOrganization). Mismatch — even
 * when the admin is a member of the resource's org — renders the
 * access-denied page (the admin UI is active-org-scoped; switch orgs to
 * view). Missing resource renders not-found. The wrapped page never mounts
 * until the check passes, so no stray effects fire against foreign ids.
 */
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { SearchX, ShieldX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useOrganization } from '@/auth/clerkShim';
import helpers from '@/utils/helpers';
import { PageHeaderSkeleton } from '@/components/Page/PageSkeletons';

export type GuardedResource =
  | 'division'
  | 'league'
  | 'tournament'
  | 'registration'
  | 'divisionTeam';

const RESOURCE_PATH: Record<GuardedResource, string> = {
  division: 'divisions',
  league: 'leagues',
  tournament: 'tournaments',
  registration: 'registrations',
  divisionTeam: 'divisionTeams',
};

type GuardState = 'checking' | 'ok' | 'denied' | 'notfound';

export function AccessErrorPage({ kind }: { kind: 'denied' | 'notfound' }) {
  const denied = kind === 'denied';
  return (
    <div className="flex min-h-[60vh] items-center justify-center p-6" data-testid="access-error-page">
      <div className="w-full max-w-md text-center">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-muted">
          {denied ? (
            <ShieldX className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
          ) : (
            <SearchX className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
          )}
        </div>
        <h1 className="mt-4 text-2xl font-semibold tracking-tight text-foreground">
          {denied ? "You don't have access to this page" : 'Page not found'}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {denied
            ? 'This page belongs to a different organization. If you manage that organization, switch to it from the organization menu and try again.'
            : "This page doesn't exist. The address may be mistyped, or the link is out of date."}
        </p>
        <div className="mt-6 flex items-center justify-center">
          <Button asChild>
            <Link to="/admin/dashboard">Go to Dashboard</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function ResourceOrgGuard({
  resource,
  param,
  children,
}: {
  resource: GuardedResource;
  param: string;
  children: React.ReactNode;
}) {
  const params = useParams();
  const id = params[param];
  const { organization, isLoaded } = useOrganization();
  const [state, setState] = useState<GuardState>('checking');

  const activeOrgId = organization?.id ? String(organization.id) : null;

  useEffect(() => {
    let cancelled = false;
    async function check() {
      if (!id) {
        setState('notfound');
        return;
      }
      if (!isLoaded || !activeOrgId) return; // wait for the active org
      setState('checking');
      try {
        const res = await fetch(`${process.env.API_URL}/api/v1/${RESOURCE_PATH[resource]}/${id}`, {
          credentials: 'include',
        });
        if (res.status === 404) {
          if (!cancelled) setState('notfound');
          return;
        }
        if (!res.ok) {
          // 401/403/5xx: can't prove access — deny rather than render.
          if (!cancelled) setState('denied');
          return;
        }
        const body = await res.json();
        const doc = body?.result ?? body;
        if (!doc || !doc._id) {
          if (!cancelled) setState('notfound');
          return;
        }
        const resourceOrgId = helpers.getObjId(doc.organization);
        if (!cancelled) {
          setState(resourceOrgId && String(resourceOrgId) === activeOrgId ? 'ok' : 'denied');
        }
      } catch {
        // Network failure: fail closed — an error page beats a foreign page.
        if (!cancelled) setState('denied');
      }
    }
    void check();
    return () => {
      cancelled = true;
    };
  }, [resource, id, activeOrgId, isLoaded]);

  if (state === 'ok') return <>{children}</>;
  if (state === 'denied') return <AccessErrorPage kind="denied" />;
  if (state === 'notfound') return <AccessErrorPage kind="notfound" />;
  return (
    <div className="p-6">
      <PageHeaderSkeleton />
    </div>
  );
}
